/**
 * RecommendedProducts
 *
 * PDP section that lazy-loads product recommendations when the user
 * scrolls near it. Renders nothing when no recommendations are available.
 */

'use client';

import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { useRecommendedProducts } from '@/lib/hooks/use-recommended-products';
import { useTranslation } from '@/lib/i18n/client';
import { ProductCarousel } from '@/components/molecules/ProductCarousel';
import {
  SkeletonContainer,
  SkeletonTitle,
  SkeletonTrack,
  SkeletonCard,
  SkeletonImage,
  SkeletonText,
} from './RecommendedProducts.styled';

export interface RecommendedProductsProps {
  productId: string;
}

const SKELETON_SLOTS = ['a', 'b', 'c', 'd'] as const;

function LoadingSkeleton() {
  return (
    <SkeletonContainer>
      <SkeletonTitle />
      <SkeletonTrack>
        {SKELETON_SLOTS.map((id) => (
          <SkeletonCard key={id}>
            <SkeletonImage />
            <SkeletonText $width="70%" />
            <SkeletonText $width="40%" />
          </SkeletonCard>
        ))}
      </SkeletonTrack>
    </SkeletonContainer>
  );
}

export function RecommendedProducts({ productId }: Readonly<RecommendedProductsProps>) {
  const { ref, isVisible } = useScrollReveal({ threshold: 0, rootMargin: '200px 0px 200px 0px' });
  const { data: products, isLoading } = useRecommendedProducts(productId, isVisible);
  const { t } = useTranslation('pdp');

  const hasProducts = products && products.length > 0;

  return (
    <div ref={ref}>
      {isVisible && isLoading && <LoadingSkeleton />}
      {hasProducts && (
        <ProductCarousel
          title={t('sections.youMayAlsoLike')}
          products={products}
        />
      )}
    </div>
  );
}
